import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";

const KINDS = [
  { key: "servicios", label: "Servicios prestados" },
  { key: "retenciones", label: "Retenciones IRPF (10-T)" },
  { key: "sepe", label: "Empresa para el SEPE" },
];
const JORNADAS = ["Jornada completa", "Jornada parcial", "Jornada parcial 50%", "Jornada parcial 75%"];
const LABEL = "text-xs font-medium uppercase tracking-wider text-slate-500";
const selectCls =
  "h-10 w-full rounded-md border border-input bg-white px-3 text-sm transition-colors hover:border-accent/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40";

export default function CertificateDialog({ open, employee, onClose, onCreated }) {
  const year = String(new Date().getFullYear() - 1);
  const [form, setForm] = useState({
    kind: "servicios", jornada: "Jornada completa", year, gross_pay: "", withheld: "",
    social_security: "", extinction_cause: "", extinction_date: "", bases: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [target, setTarget] = useState("worker");
  const [customEmail, setCustomEmail] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/certificates/${created.id}/send`, { target, email: customEmail });
      toast.success(`Certificado enviado a ${data.to}`);
      close();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const close = () => { setCreated(null); setCustomEmail(""); setTarget("worker"); onClose(); };

  const generate = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        gross_pay: Number(form.gross_pay) || 0,
        withheld: Number(form.withheld) || 0,
        social_security: Number(form.social_security) || 0,
      };
      const { data } = await api.post(`/employees/${employee.id}/certificate`, payload);
      const res = await api.get(`/files/${data.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = data.original_filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("Certificado generado y guardado en sus documentos");
      setCreated(data);
      onCreated?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!employee) return null;

  if (created)
    return (
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md w-[calc(100vw-1rem)] sm:w-full p-5 sm:p-6" data-testid="certificate-send-step">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Enviar por correo</DialogTitle>
            <DialogDescription>
              El certificado ya está descargado y guardado en los documentos de {employee.name}. Si quieres, lo envío con el PDF adjunto.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[["worker", "Al trabajador (email de su ficha)"], ["gestoria", "A la gestoría (email de Ajustes)"], ["custom", "A otra dirección"]].map(([k, l]) => (
              <label key={k} className="flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm transition-colors hover:border-accent/60">
                <input type="radio" className="h-4 w-4 accent-primary" checked={target === k} onChange={() => setTarget(k)}
                  data-testid={`certificate-target-${k}`} />
                {l}
              </label>
            ))}
            {target === "custom" && (
              <Input type="email" placeholder="correo@ejemplo.es" value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)} data-testid="certificate-custom-email" />
            )}
          </div>
          <DialogFooter className="flex-row justify-end gap-2">
            <Button variant="outline" onClick={close} disabled={busy}>No enviar</Button>
            <Button onClick={send} disabled={busy} data-testid="certificate-send">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />} Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100vw-1rem)] sm:w-full max-h-[92vh] overflow-y-auto p-5 sm:p-6" data-testid="certificate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Certificado de empresa</DialogTitle>
          <DialogDescription>
            Para {employee.name}. Se firma con el sello de empresa de Ajustes y queda guardado en sus documentos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className={LABEL}>Tipo de certificado</Label>
            <select className={selectCls} value={form.kind} onChange={(e) => set("kind", e.target.value)} data-testid="certificate-kind">
              {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label className={LABEL}>Jornada</Label>
            <select className={selectCls} value={form.jornada} onChange={(e) => set("jornada", e.target.value)} data-testid="certificate-jornada">
              {JORNADAS.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
          </div>

          {form.kind === "retenciones" && (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="certificate-irpf-fields">
              <div className="space-y-1.5">
                <Label className={LABEL}>Ejercicio</Label>
                <Input value={form.year} onChange={(e) => set("year", e.target.value)} data-testid="certificate-year" />
              </div>
              <div className="space-y-1.5">
                <Label className={LABEL}>Retribuciones íntegras (€)</Label>
                <Input type="number" step="0.01" value={form.gross_pay} onChange={(e) => set("gross_pay", e.target.value)} data-testid="certificate-gross" />
              </div>
              <div className="space-y-1.5">
                <Label className={LABEL}>Retenciones IRPF (€)</Label>
                <Input type="number" step="0.01" value={form.withheld} onChange={(e) => set("withheld", e.target.value)} data-testid="certificate-withheld" />
              </div>
              <div className="space-y-1.5">
                <Label className={LABEL}>Seguridad Social trabajador (€)</Label>
                <Input type="number" step="0.01" value={form.social_security} onChange={(e) => set("social_security", e.target.value)} data-testid="certificate-ss" />
              </div>
            </div>
          )}

          {form.kind === "sepe" && (
            <div className="space-y-4" data-testid="certificate-sepe-fields">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className={LABEL}>Causa de la extinción</Label>
                  <Input value={form.extinction_cause} placeholder="Fin de contrato, despido objetivo…"
                    onChange={(e) => set("extinction_cause", e.target.value)} data-testid="certificate-cause" />
                </div>
                <div className="space-y-1.5">
                  <Label className={LABEL}>Fecha de efectos</Label>
                  <Input type="date" value={form.extinction_date} onChange={(e) => set("extinction_date", e.target.value)} data-testid="certificate-date" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className={LABEL}>Bases de cotización (una por línea)</Label>
                <Textarea rows={4} value={form.bases} placeholder={"Junio 2026: 1.850,00 €\nMayo 2026: 1.850,00 €"}
                  onChange={(e) => set("bases", e.target.value)} data-testid="certificate-bases" />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className={LABEL}>Observaciones (opcional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="certificate-notes" />
          </div>
        </div>

        <DialogFooter className="flex-row justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={generate} disabled={busy} data-testid="certificate-generate">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Generar y descargar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
